import React from 'react';
import type { ElementType, ReactElement, MouseEvent } from 'react';

import classnames from 'classnames';

import { Overflow } from '../../../Overflow/Overflow';
import {
    useBreadcrumbContext,
    type BreadcrumbItem as BreadcrumbItemType
} from '../../Breadcrumb.context';
import * as Breadcrumb from '../../Breadcrumb.parts';

import styles from './BreadcrumbItem.module.scss';

type BreadcrumbItemProps = {
    className?: string;
    item: BreadcrumbItemType;
    onItemClick?: (id: string) => void;
    as?: ElementType;
    divider?: ReactElement;
};

export const BreadcrumbItem = ({
    className,
    as = 'li',
    item: { id, label, href, onClick },
    onItemClick,
    divider,
    ...rest
}: BreadcrumbItemProps): ReactElement => {
    const { overflow } = useBreadcrumbContext();
    const isCurrentPage = overflow.isLastItem(id);
    const showDivider = !overflow.isFirstItem(id) || overflow.hasOverflow;

    const handleClick = (
        event: MouseEvent<HTMLButtonElement> & MouseEvent<HTMLAnchorElement, MouseEvent>
    ) => {
        onClick?.(event);

        if (onItemClick) {
            event.preventDefault();
            onItemClick(id);
        }
    };
    return (
        <Overflow.Item
            as={as}
            className={classnames(styles.container, className)}
            id={id}
            {...(isCurrentPage ? { 'aria-current': 'page' } : {})}
            {...rest}
        >
            {showDivider ? divider ?? <Breadcrumb.Divider /> : null}
            {isCurrentPage ? (
                <Breadcrumb.ItemButton className={styles.currentPage}>
                    <Breadcrumb.Text className={styles.itemText}>{label}</Breadcrumb.Text>
                </Breadcrumb.ItemButton>
            ) : (
                <Breadcrumb.ItemButton onClick={handleClick} href={href}>
                    <Breadcrumb.Text>{label}</Breadcrumb.Text>
                </Breadcrumb.ItemButton>
            )}
        </Overflow.Item>
    );
};
